import React, { useState, useRef, useEffect } from 'react';
import { Expense, ExpenseCategory } from '../types/expense';
import { Invoice } from '../types/invoice';

interface Message {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

interface AIAssistantProps {
  expenses: Expense[];
  income: number;
  auditRiskScore: number;
  riskLevel: string;
  isPremium: boolean;
  invoices?: Invoice[];
}

export default function AIAssistant({
  expenses,
  income,
  auditRiskScore,
  riskLevel,
  isPremium,
  invoices = [],
}: AIAssistantProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    {
      role: 'assistant',
      content: '👋 Hi! I\'m your AI tax assistant. I can help you optimize deductions, understand your audit risk, and find tax-saving opportunities. What would you like to know?',
      timestamp: new Date(),
    },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [messageCount, setMessageCount] = useState(0);
  const MAX_FREE_MESSAGES = 5;

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const resetChat = () => {
    setMessages([
      {
        role: 'assistant',
        content: '👋 Hi! I\'m your AI tax assistant. I can help you optimize deductions, understand your audit risk, and find tax-saving opportunities. What would you like to know?',
        timestamp: new Date(),
      },
    ]);
    setMessageCount(0);
  };

  // Prepare user data for context
  const getUserData = () => {
    const categoryTotals = expenses.reduce((acc, exp) => {
      acc[exp.category] = (acc[exp.category] || 0) + exp.amount;
      return acc;
    }, {} as Record<string, number>);

    const topCategories = Object.entries(categoryTotals)
      .map(([category, value]) => ({ name: category, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 5);

    return {
      income,
      totalExpenses: expenses.reduce((sum, e) => sum + e.amount, 0),
      expenseCount: expenses.length,
      auditRiskScore,
      riskLevel,
      isPremium,
      categories: Object.keys(categoryTotals),
      topCategories,
      invoiceCount: invoices.length,
      totalRevenue: invoices.reduce((sum, inv) => sum + inv.total, 0),
    };
  };

  const quickPrompts = [
    { icon: '🎯', text: 'Analyze my audit risk', prompt: 'Analyze my current audit risk and suggest how to reduce it' },
    { icon: '💡', text: 'Missing deductions?', prompt: 'What common 1099 deductions might I be missing?' },
    { icon: '📊', text: 'Optimize expenses', prompt: 'Review my expenses and suggest optimizations' },
    { icon: '💰', text: 'Tax saving tips', prompt: 'What are my best tax-saving opportunities?' },
  ];

  const sendMessage = async (messageText: string) => {
    if (!messageText.trim() || loading) return;

    // Check message limit for free users
    if (!isPremium && messageCount >= MAX_FREE_MESSAGES) {
      alert('You\'ve reached the free message limit. Upgrade to Premium for unlimited AI assistance!');
      return;
    }

    const userMessage: Message = {
      role: 'user',
      content: messageText,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setLoading(true);
    setMessageCount((prev) => prev + 1);

    try {
      const response = await fetch('/.netlify/functions/ai-assistant', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: messageText,
          userData: getUserData(),
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to get response');
      }

      const data = await response.json();

      const assistantMessage: Message = {
        role: 'assistant',
        content: data.response,
        timestamp: new Date(data.timestamp),
      };

      setMessages((prev) => [...prev, assistantMessage]);
    } catch (error) {
      const errorMessage: Message = {
        role: 'assistant',
        content: '❌ Sorry, I encountered an error. Please try again or contact support if the issue persists.',
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleQuickPrompt = (prompt: string) => {
    sendMessage(prompt);
  };

  return (
    <>
      {/* Floating Chat Bubble */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 w-16 h-16 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-full shadow-2xl hover:shadow-3xl hover:scale-110 transition-all z-50 flex items-center justify-center group"
          aria-label="Open AI Assistant"
        >
          <span className="text-2xl">🤖</span>
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-xs font-bold animate-pulse">
            AI
          </span>
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-6 right-6 w-96 h-[600px] bg-white rounded-2xl shadow-2xl z-50 flex flex-col overflow-hidden border-2 border-purple-200">
          {/* Header */}
          <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center text-xl">
                🤖
              </div>
              <div>
                <h3 className="font-bold">AI Tax Assistant</h3>
                <p className="text-xs text-purple-100">
                  {isPremium ? 'Unlimited' : `${MAX_FREE_MESSAGES - messageCount} messages left`}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={resetChat}
                className="px-3 py-1.5 bg-white/20 hover:bg-white/30 rounded-lg transition-colors flex items-center gap-1.5 text-sm font-semibold"
                title="Start a new conversation"
              >
                <span className="text-base">🔄</span>
                <span>New Chat</span>
              </button>
              <button
                onClick={() => setIsOpen(false)}
                className="w-8 h-8 hover:bg-white/20 rounded-lg transition-colors flex items-center justify-center"
              >
                ✕
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                    msg.role === 'user'
                      ? 'bg-purple-600 text-white rounded-br-none'
                      : 'bg-white text-gray-800 rounded-bl-none shadow-md border border-gray-200'
                  }`}
                >
                  <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                  <span className="text-xs opacity-60 mt-1 block">
                    {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-white rounded-2xl rounded-bl-none px-4 py-3 shadow-md border border-gray-200">
                  <div className="flex gap-1">
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></span>
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></span>
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Quick Prompts */}
          {messages.length === 1 && (
            <div className="p-3 bg-white border-t border-gray-200">
              <p className="text-xs text-gray-600 mb-2 font-semibold">Quick questions:</p>
              <div className="grid grid-cols-2 gap-2">
                {quickPrompts.map((prompt, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleQuickPrompt(prompt.prompt)}
                    className="text-xs bg-purple-50 hover:bg-purple-100 text-purple-700 rounded-lg px-3 py-2 transition-colors text-left font-medium border border-purple-200"
                  >
                    <span className="mr-1">{prompt.icon}</span>
                    {prompt.text}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input */}
          <div className="p-4 bg-white border-t border-gray-200">
            <div className="flex gap-2">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && sendMessage(input)}
                placeholder="Ask me anything..."
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-600 text-sm"
                disabled={loading}
              />
              <button
                onClick={() => sendMessage(input)}
                disabled={loading || !input.trim()}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-semibold text-sm"
              >
                {loading ? '...' : '📤'}
              </button>
            </div>
            {!isPremium && (
              <p className="text-xs text-gray-500 mt-2 text-center">
                {MAX_FREE_MESSAGES - messageCount} free messages remaining
              </p>
            )}
          </div>
        </div>
      )}
    </>
  );
}
